import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Search, AlertTriangle, ExternalLink, FlaskConical, Trash2, Gauge } from "lucide-react";
import {
    getCampaign,
    patchCampaign,
    deleteCampaign,
    listProspects,
    listVerticalTrackers,
    googlePlacesStatus,
    findProspects,
    deleteDryRunProspects,
    scoreAllProspects,
    CAMPAIGN_STATUSES,
    CAMPAIGN_STATUS_COLORS,
    STATUS_COLORS,
} from "@/lib/chiroApi";
import ProspectDetail from "@/components/ProspectDetail";
import ScorePill from "@/components/ScorePill";

export default function CampaignDetail() {
    const { id } = useParams();
    const [c, setC] = useState(null);
    const [trackers, setTrackers] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [zipsText, setZipsText] = useState("");
    const [disqText, setDisqText] = useState("");
    const [saving, setSaving] = useState(false);
    const [places, setPlaces] = useState({ configured: null, message: "" });
    const [finding, setFinding] = useState(false);
    const [lastFindResult, setLastFindResult] = useState(null);
    const [selected, setSelected] = useState(null);
    const [openDetail, setOpenDetail] = useState(false);
    // iter28 — default to Opportunity-descending sort and show "outreach-worthy
    // only" (opp >= 10 = tier Weak/Moderate/Strong) filter.
    const [sortByOpp, setSortByOpp] = useState(true);
    const [outreachWorthyOnly, setOutreachWorthyOnly] = useState(false);
    // Dry-run toggle is the source of truth for the next Find Prospects call.
    // Defaults to whatever the campaign has stored (which itself defaults true).
    const [dryRunToggle, setDryRunToggle] = useState(true);

    const loadAll = async () => {
        const [campaign, t, p] = await Promise.all([
            getCampaign(id),
            listVerticalTrackers(),
            listProspects({ campaign_id: id }),
        ]);
        setC(campaign);
        setTrackers(t);
        setProspects(p);
        setZipsText((campaign.target_zips || []).join(", "));
        setDisqText((campaign.disqualifiers || []).join("\n"));
        setDryRunToggle(campaign.dry_run !== false); // default to ON
    };

    useEffect(() => {
        loadAll();
        googlePlacesStatus().then(setPlaces);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const dryRunCount = useMemo(
        () => prospects.filter((p) => p.is_dry_run).length,
        [prospects],
    );

    const sortedProspects = useMemo(() => {
        let arr = prospects;
        if (outreachWorthyOnly) {
            arr = arr.filter((p) => (p.opportunity_score ?? 0) >= 10);
        }
        if (!sortByOpp) return arr;
        return [...arr].sort((a, b) => {
            const av = a.opportunity_score ?? -1;
            const bv = b.opportunity_score ?? -1;
            return bv - av;
        });
    }, [prospects, sortByOpp, outreachWorthyOnly]);

    if (!c) {
        return (
            <div className="font-mono text-[12px] text-[#9ca3af]" data-testid="campaign-loading">
                Loading…
            </div>
        );
    }

    const update = (k, v) => setC((cur) => ({ ...cur, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                name: c.name,
                city: c.city,
                target_state: c.target_state,
                target_business_type: c.target_business_type,
                vertical_tracker_id: c.vertical_tracker_id,
                goals: c.goals,
                cta: c.cta,
                max_prospects: c.max_prospects ? Number(c.max_prospects) : null,
                status: c.status,
                dry_run: dryRunToggle,
                target_zips: zipsText
                    .split(/[,\n]/)
                    .map((s) => s.trim())
                    .filter(Boolean),
                disqualifiers: disqText
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean),
            };
            const next = await patchCampaign(id, body);
            setC(next);
            toast.success("Campaign saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!confirm("Delete this campaign and all its prospects?")) return;
        try {
            await deleteCampaign(id);
            toast.success("Campaign deleted");
            window.location.href = "/campaigns";
        } catch {
            toast.error("Delete failed");
        }
    };

    const handleFind = async () => {
        if (!places.configured) {
            toast.error("Google Places key not configured");
            return;
        }
        setFinding(true);
        setLastFindResult(null);
        try {
            const r = await findProspects(id, { dry_run: dryRunToggle });
            setLastFindResult(r);
            const label = r.dry_run ? "Dry-run" : "Live";
            toast.success(
                `${label}: imported ${r.imported} (${r.skipped_existing} duplicates skipped)`,
            );
            const p = await listProspects({ campaign_id: id });
            setProspects(p);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object") {
                setLastFindResult({ error: detail });
                toast.error(detail.message || "Find failed");
            } else {
                toast.error(detail || "Find failed");
            }
        } finally {
            setFinding(false);
        }
    };

    const handleDeleteDryRun = async () => {
        if (dryRunCount === 0) return;
        if (
            !confirm(
                `Delete all ${dryRunCount} dry-run prospect(s) on this campaign? Non-dry-run prospects will not be touched.`,
            )
        )
            return;
        try {
            const r = await deleteDryRunProspects(id);
            toast.success(`Deleted ${r.deleted} dry-run prospect(s)`);
            const p = await listProspects({ campaign_id: id });
            setProspects(p);
        } catch {
            toast.error("Delete failed");
        }
    };

    const handleScoreAll = async () => {
        try {
            const r = await scoreAllProspects(id);
            toast.success(
                `Scored ${r.scored} (disqualified: ${r.disqualified}, not operational: ${r.not_operational})`,
            );
            const p = await listProspects({ campaign_id: id });
            setProspects(p);
        } catch {
            toast.error("Score failed");
        }
    };

    return (
        <div data-testid="campaign-detail-page" className="max-w-5xl">
            <Link
                to="/campaigns"
                className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] text-[#9ca3af] hover:text-[#f3f4f6] mb-3"
                data-testid="campaign-back"
            >
                <ArrowLeft className="w-3 h-3" /> all campaigns
            </Link>

            <div className="flex items-baseline justify-between mb-1">
                <h1 className="text-3xl font-bold tracking-tight">{c.name}</h1>
                <span
                    className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-[2px] border rounded-[2px]"
                    style={{
                        color: CAMPAIGN_STATUS_COLORS[c.status] || "#9ca3af",
                        borderColor: (CAMPAIGN_STATUS_COLORS[c.status] || "#262a2e") + "55",
                    }}
                    data-testid="campaign-status-pill"
                >
                    {c.status || "draft"}
                </span>
            </div>
            <div className="font-mono text-[11px] text-[#6b7280] mb-6">id · {c.id}</div>

            {/* Find Prospects banner */}
            <div className="mb-6 p-4 border border-[#262a2e] bg-[#0f1113] rounded-[2px]">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#7cc93c] mb-1">
                            Find Prospects · Google Places
                        </div>
                        {places.configured === false && (
                            <div
                                className="text-[12px] text-[#ff9f0a] flex items-center gap-1"
                                data-testid="places-missing-banner"
                            >
                                <AlertTriangle className="w-3.5 h-3.5" />
                                Google Places key not configured. Set{" "}
                                <code className="font-mono">GOOGLE_PLACES_API_KEY</code> in
                                backend/.env and restart the backend. No prospects will be created.
                            </div>
                        )}
                        {places.configured === true && (
                            <div
                                className="text-[12px] text-[#9ca3af]"
                                data-testid="places-ok-banner"
                            >
                                Real Google Places API. Pulls businesses by query "
                                {c.target_business_type || "(business type)"} in{" "}
                                {(c.target_zips || []).length
                                    ? `${(c.target_zips || []).length} ZIP(s)`
                                    : `${c.city || "—"} ${c.target_state || ""}`.trim() || "—"}
                                ", deduped by place_id → name+zip → domain.
                            </div>
                        )}

                        {/* Dry run toggle */}
                        <label
                            className={`mt-3 flex items-start gap-3 p-3 rounded-[2px] cursor-pointer border transition-colors ${
                                dryRunToggle
                                    ? "border-[#0a84ff]/50 bg-[#0a84ff]/8"
                                    : "border-[#262a2e] bg-[#0a0b0d]"
                            }`}
                            data-testid="dry-run-toggle-row"
                        >
                            <input
                                type="checkbox"
                                checked={dryRunToggle}
                                onChange={(e) => setDryRunToggle(e.target.checked)}
                                data-testid="dry-run-toggle"
                                className="mt-0.5 accent-[#0a84ff]"
                            />
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <FlaskConical
                                        className={`w-3.5 h-3.5 ${
                                            dryRunToggle ? "text-[#0a84ff]" : "text-[#9ca3af]"
                                        }`}
                                    />
                                    <span className="font-mono text-[11px] uppercase tracking-[0.15em]">
                                        Dry run · cap to 5 prospects
                                    </span>
                                    {dryRunToggle && (
                                        <span
                                            className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#0a84ff] border border-[#0a84ff]/40 px-1.5 py-[1px]"
                                            data-testid="dry-run-on-pill"
                                        >
                                            ON
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-[#9ca3af] mt-1">
                                    Test the query and dedup before pulling a full batch.
                                    Imported prospects will be tagged{" "}
                                    <span className="text-[#0a84ff]">DRY RUN</span> and easy to
                                    delete. <strong>Does not launch the campaign.</strong>{" "}
                                    Outreach is never auto-sent.
                                </div>
                            </div>
                        </label>
                    </div>
                    <button
                        onClick={handleFind}
                        disabled={finding || places.configured !== true}
                        data-testid="find-prospects-btn"
                        className="flex items-center gap-2 bg-[#7cc93c] text-[#090a0b] hover:bg-[#9be05c] disabled:opacity-40 disabled:cursor-not-allowed font-mono text-[12px] uppercase tracking-[0.15em] px-4 py-2 rounded-[2px] shrink-0 self-start"
                    >
                        <Search className="w-3.5 h-3.5" />
                        {finding
                            ? "Searching…"
                            : dryRunToggle
                              ? "Find Prospects (Dry Run · 5)"
                              : "Find Prospects"}
                    </button>
                </div>

                {/* Last find result */}
                {lastFindResult && !lastFindResult.error && (
                    <div
                        className={`mt-3 pt-3 border-t border-[#262a2e] font-mono text-[11px] ${
                            lastFindResult.dry_run ? "text-[#0a84ff]" : "text-[#9ca3af]"
                        }`}
                        data-testid="find-result"
                    >
                        {lastFindResult.dry_run && (
                            <div className="mb-1 inline-flex items-center gap-1 text-[#0a84ff] uppercase tracking-[0.2em] text-[10px]">
                                <FlaskConical className="w-3 h-3" /> Dry-run results · campaign
                                NOT launched
                            </div>
                        )}
                        <div className="text-[#9ca3af]">
                            Imported {lastFindResult.imported} · Skipped duplicates{" "}
                            {lastFindResult.skipped_existing} · Cap {lastFindResult.max_prospects} ·
                            API calls {(lastFindResult.api_calls || []).length} · Queries:{" "}
                            {(lastFindResult.queries || []).join(" | ") || "—"}
                            {lastFindResult.receipt_id && (
                                <>
                                    {" "}
                                    · Receipt:{" "}
                                    <Link
                                        to="/log"
                                        className="text-[#7cc93c] hover:underline"
                                        data-testid="find-result-receipt-link"
                                    >
                                        {lastFindResult.receipt_id.slice(0, 8)}
                                    </Link>
                                </>
                            )}
                        </div>
                        {(lastFindResult.errors || []).length > 0 && (
                            <div className="text-[#ff9f0a] mt-1">
                                Errors: {(lastFindResult.errors || []).join(" / ")}
                            </div>
                        )}
                    </div>
                )}
                {lastFindResult?.error && (
                    <div
                        className="mt-3 pt-3 border-t border-[#262a2e] font-mono text-[11px] text-[#ff453a]"
                        data-testid="find-error"
                    >
                        {lastFindResult.error.message ||
                            lastFindResult.error.reason ||
                            "Unknown error"}
                    </div>
                )}

                {/* Manage dry-run prospects */}
                {dryRunCount > 0 && (
                    <div
                        className="mt-3 pt-3 border-t border-[#262a2e] flex items-center justify-between gap-3"
                        data-testid="dry-run-manage"
                    >
                        <div className="font-mono text-[11px] text-[#0a84ff]">
                            <FlaskConical className="w-3 h-3 inline mr-1" />
                            {dryRunCount} dry-run prospect{dryRunCount === 1 ? "" : "s"} on this
                            campaign
                        </div>
                        <button
                            onClick={handleDeleteDryRun}
                            data-testid="delete-dry-run-btn"
                            className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#ff453a] border border-[#ff453a]/40 hover:bg-[#ff453a]/10 px-3 py-1.5 rounded-[2px]"
                        >
                            <Trash2 className="w-3 h-3" />
                            Delete dry-run prospects
                        </button>
                    </div>
                )}
            </div>

            {/* Settings */}
            <div className="grid grid-cols-2 gap-3 mb-4">
                <Field
                    label="Name"
                    value={c.name}
                    onChange={(v) => update("name", v)}
                    testid="campaign-name"
                />
                <Select
                    label="Status"
                    value={c.status || "draft"}
                    onChange={(v) => update("status", v)}
                    testid="campaign-status"
                    options={CAMPAIGN_STATUSES.map((s) => ({ value: s, label: s }))}
                />
                <Select
                    label="Vertical Tracker"
                    value={c.vertical_tracker_id || ""}
                    onChange={(v) => update("vertical_tracker_id", v)}
                    testid="campaign-tracker"
                    options={[
                        { value: "", label: "— none —" },
                        ...trackers.map((t) => ({
                            value: t.id,
                            label: `${t.name}${t.status === "coming_later" ? " (soon)" : ""}`,
                            disabled: t.status === "coming_later",
                        })),
                    ]}
                />
                <Field
                    label="Target business type"
                    value={c.target_business_type || ""}
                    onChange={(v) => update("target_business_type", v)}
                    testid="campaign-business-type"
                />
                <Field
                    label="City"
                    value={c.city || ""}
                    onChange={(v) => update("city", v)}
                    testid="campaign-city"
                />
                <Field
                    label="Target state (e.g. TN)"
                    value={c.target_state || ""}
                    onChange={(v) => update("target_state", v.toUpperCase())}
                    testid="campaign-state"
                />
                <Field
                    label="Max prospects per LIVE run (dry-run = 5; live capped at 50)"
                    value={c.max_prospects ?? ""}
                    onChange={(v) => update("max_prospects", v.replace(/\D/g, ""))}
                    testid="campaign-max"
                />
                <Field
                    label="CTA"
                    value={c.cta || ""}
                    onChange={(v) => update("cta", v)}
                    testid="campaign-cta"
                />
            </div>

            <div className="mb-4">
                <FieldArea
                    label="Target ZIPs (comma or newline separated, max 5 used per run)"
                    rows={2}
                    testid="campaign-zips"
                    value={zipsText}
                    onChange={setZipsText}
                />
            </div>
            <div className="mb-4">
                <FieldArea
                    label="Goals (what this campaign is trying to achieve)"
                    rows={3}
                    testid="campaign-goals"
                    value={c.goals || ""}
                    onChange={(v) => update("goals", v)}
                />
            </div>
            <div className="mb-4">
                <FieldArea
                    label="Disqualifiers (one per line)"
                    rows={3}
                    testid="campaign-disqualifiers"
                    value={disqText}
                    onChange={setDisqText}
                />
            </div>

            <div className="mt-2 mb-8 flex items-center gap-3">
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="campaign-save-btn"
                    className="flex items-center gap-2 bg-[#7cc93c] text-[#090a0b] hover:bg-[#9be05c] disabled:opacity-50 font-mono text-[12px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-[2px]"
                >
                    <Save className="w-3.5 h-3.5" />
                    {saving ? "Saving…" : "Save campaign"}
                </button>
                <button
                    onClick={remove}
                    data-testid="campaign-delete-btn"
                    className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#ff453a] border border-[#ff453a]/40 hover:bg-[#ff453a]/10 px-3 py-2 rounded-[2px]"
                >
                    Delete
                </button>
                <span className="font-mono text-[11px] text-[#6b7280] ml-auto">
                    {prospects.length} prospects on this campaign
                </span>
            </div>

            {/* Prospects table */}
            <div data-testid="campaign-prospects">
                {/* iter28 — Opportunity tier summary counts */}
                {prospects.length > 0 && c.opp_tier_counts && (
                    <div
                        className="mb-3 grid grid-cols-5 gap-2"
                        data-testid="campaign-tier-summary"
                    >
                        <TierSummaryChip
                            label="Total"
                            count={c.prospect_count || prospects.length}
                            color="neutral"
                            testid="tier-summary-total"
                        />
                        <TierSummaryChip
                            label="Strong opportunities"
                            count={c.opp_tier_counts.strong}
                            color="strong"
                            testid="tier-summary-strong"
                        />
                        <TierSummaryChip
                            label="Moderate opportunities"
                            count={c.opp_tier_counts.moderate}
                            color="moderate"
                            testid="tier-summary-moderate"
                        />
                        <TierSummaryChip
                            label="Weak hooks"
                            count={c.opp_tier_counts.weak}
                            color="weak"
                            testid="tier-summary-weak"
                        />
                        <TierSummaryChip
                            label="No visible hook"
                            count={c.opp_tier_counts.no_hook}
                            color="no_hook"
                            testid="tier-summary-no-hook"
                        />
                    </div>
                )}
                <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9ca3af]">
                        Prospects in this campaign
                    </div>
                    <div className="flex items-center gap-2">
                        {prospects.length > 1 && (
                            <button
                                onClick={() => setOutreachWorthyOnly((s) => !s)}
                                data-testid="campaign-outreach-worthy-only"
                                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border rounded-[2px] transition-colors ${
                                    outreachWorthyOnly
                                        ? "border-[#7cc93c] text-[#7cc93c]"
                                        : "border-[#262a2e] text-[#9ca3af] hover:text-[#f3f4f6]"
                                }`}
                                title="Show only prospects with Opp ≥ 10 (tier Weak, Moderate, or Strong)."
                            >
                                Outreach-worthy only {outreachWorthyOnly ? "ON" : "OFF"}
                            </button>
                        )}
                        {prospects.length > 1 && (
                            <button
                                onClick={() => setSortByOpp((s) => !s)}
                                data-testid="campaign-sort-by-opp"
                                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border rounded-[2px] transition-colors ${
                                    sortByOpp
                                        ? "border-[#7cc93c] text-[#7cc93c]"
                                        : "border-[#262a2e] text-[#9ca3af] hover:text-[#f3f4f6]"
                                }`}
                            >
                                Sort by Opp ↓ {sortByOpp ? "ON" : "OFF"}
                            </button>
                        )}
                        {prospects.length > 0 && (
                            <button
                                onClick={handleScoreAll}
                                data-testid="score-all-btn"
                                className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#9ca3af] border border-[#262a2e] hover:border-[#7cc93c] hover:text-[#7cc93c] px-3 py-1.5 rounded-[2px]"
                            >
                                <Gauge className="w-3 h-3" />
                                Score all
                            </button>
                        )}
                    </div>
                </div>
                {prospects.length === 0 && (
                    <div className="p-6 border border-dashed border-[#262a2e] text-center text-[#9ca3af] font-mono text-[12px]">
                        No prospects yet. Run Find Prospects above (when the key is configured).
                    </div>
                )}
                {prospects.length > 0 && (
                    <div className="border border-[#262a2e] rounded-[2px] divide-y divide-[#262a2e]">
                        {sortedProspects.map((p) => (
                            <button
                                key={p.id}
                                onClick={() => {
                                    setSelected(p);
                                    setOpenDetail(true);
                                }}
                                data-testid={`prospect-row-${p.id}`}
                                className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[#0f1113]"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-[13px] truncate flex items-center gap-2">
                                        {p.practice_name}
                                        {p.is_dry_run && (
                                            <span
                                                className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#0a84ff] border border-[#0a84ff]/40 px-1 py-[1px] rounded-[2px] shrink-0"
                                                data-testid={`prospect-dry-run-pill-${p.id}`}
                                            >
                                                DRY RUN
                                            </span>
                                        )}
                                        {p.disqualified && (
                                            <span
                                                className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#ff453a] border border-[#ff453a]/40 px-1 py-[1px] rounded-[2px] shrink-0"
                                                data-testid={`prospect-disqualified-pill-${p.id}`}
                                            >
                                                DISQUALIFIED
                                            </span>
                                        )}
                                    </div>
                                    <div className="font-mono text-[10px] text-[#9ca3af] truncate">
                                        {p.city || "—"}, {p.state || "—"} {p.zip || ""} ·{" "}
                                        {p.website || "no website"}
                                    </div>
                                </div>
                                <ScorePill
                                    label="Fit"
                                    value={p.fit_score}
                                    testid={`prospect-fit-${p.id}`}
                                />
                                <ScorePill
                                    label="Opp"
                                    value={p.opportunity_score}
                                    testid={`prospect-opp-${p.id}`}
                                />
                                {p.opportunity_tier_label && (
                                    <span
                                        data-testid={`prospect-opp-tier-${p.id}`}
                                        className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2 py-[2px] border rounded-[2px] shrink-0 ${
                                            TIER_CHIP_STYLES[p.opportunity_tier] ||
                                            TIER_CHIP_STYLES.neutral
                                        }`}
                                        title={`Opportunity ${p.opportunity_score ?? 0} — ${p.opportunity_tier_label}`}
                                    >
                                        {p.opportunity_tier_label}
                                    </span>
                                )}
                                {p.google_rating && (
                                    <div className="font-mono text-[10px] text-[#9ca3af] shrink-0">
                                        ★ {p.google_rating} ({p.google_user_rating_count || 0})
                                    </div>
                                )}
                                <span
                                    className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-[2px] border rounded-[2px] shrink-0"
                                    style={{
                                        color: STATUS_COLORS[p.status] || "#9ca3af",
                                        borderColor: (STATUS_COLORS[p.status] || "#262a2e") + "55",
                                    }}
                                >
                                    {p.status}
                                </span>
                                {p.gbp_link && (
                                    <a
                                        href={p.gbp_link}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-[#9ca3af] hover:text-[#7cc93c] shrink-0"
                                        data-testid={`prospect-gbp-${p.id}`}
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {selected && (
                <ProspectDetail
                    open={openDetail}
                    onOpenChange={setOpenDetail}
                    prospect={selected}
                    onChanged={(p) => {
                        setProspects((list) => list.map((x) => (x.id === p.id ? p : x)));
                        setSelected(p);
                    }}
                    onDeleted={(pid) => {
                        setProspects((list) => list.filter((x) => x.id !== pid));
                        setOpenDetail(false);
                    }}
                />
            )}
        </div>
    );
}

function Field({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="block font-mono text-[11px] tracking-[0.2em] uppercase text-[#9ca3af] mb-1">
                {label}
            </label>
            <input
                type="text"
                data-testid={testid}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] focus:outline-none focus:border-[#7cc93c]"
            />
        </div>
    );
}

function FieldArea({ label, value, onChange, rows = 3, testid }) {
    return (
        <div>
            <label className="block font-mono text-[11px] tracking-[0.2em] uppercase text-[#9ca3af] mb-1">
                {label}
            </label>
            <textarea
                rows={rows}
                data-testid={testid}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] focus:outline-none focus:border-[#7cc93c]"
            />
        </div>
    );
}

function Select({ label, value, onChange, testid, options }) {
    return (
        <div>
            <label className="block font-mono text-[11px] tracking-[0.2em] uppercase text-[#9ca3af] mb-1">
                {label}
            </label>
            <select
                data-testid={testid}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] focus:outline-none focus:border-[#7cc93c]"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value} disabled={o.disabled}>
                        {o.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

// iter28 — per-tier summary chip at the top of the prospects table.
// Colour palette matches the rest of the dark/monospace aesthetic; no emojis.
const TIER_CHIP_STYLES = {
    neutral:  "border-[#262a2e] text-[#e5e7eb]",
    strong:   "border-[#7cc93c]/60 text-[#9be05c]",
    moderate: "border-[#f0c857]/60 text-[#f7d77a]",
    weak:     "border-[#e59866]/50 text-[#eab286]",
    no_hook:  "border-[#262a2e] text-[#6b7280]",
};

function TierSummaryChip({ label, count, color, testid }) {
    const klass = TIER_CHIP_STYLES[color] || TIER_CHIP_STYLES.neutral;
    return (
        <div
            data-testid={testid}
            className={`border rounded-[2px] px-3 py-2 bg-[#0a0b0d] ${klass}`}
        >
            <div className="font-mono text-[20px] leading-none">
                {count ?? 0}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] mt-1 opacity-70">
                {label}
            </div>
        </div>
    );
}

